/* InvoiceDetailDialog — view, send, record payment, edit, void, refund,
 * mark-uncollectible, print.
 *
 * Single dialog handles every action the doctor can take on an existing
 * invoice. Status drives which actions are available (iter98.99x):
 *   draft         → Edit, Send
 *   sent / viewed → Record payment, Refund (n/a yet), Edit, Void, Uncollectible, Print
 *   partial       → Record payment, Refund, Edit, Void, Uncollectible, Print
 *   paid          → Refund, Print only (immutable line items)
 *   refunded      → Print only (immutable)
 *   uncollectible → Print only (immutable)
 *   void          → Print only (immutable)
 */
import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
    Receipt, Pencil, Printer, X, CheckCircle, Money, WhatsappLogo,
    ArrowsCounterClockwise, ProhibitInset, QrCode,
} from "@phosphor-icons/react";
import { QRCodeSVG } from "qrcode.react";
import InvoiceFormDialog from "./InvoiceFormDialog";
import InvoicePrintView from "./InvoicePrintView";
import RecordPaymentDialog from "./RecordPaymentDialog";
import { confirmDialog, alertDialog, promptDialog } from "@/lib/confirm";

const STATUS_TONE = {
    /* iter98.99ad — Draft + Sent both render as "Unpaid" (draft state
     * retired; legacy drafts auto-migrated on backend list-read). */
    draft:         { label: "Unpaid",        bg: "bg-primary/10 text-primary border-primary/20" },
    sent:          { label: "Unpaid",        bg: "bg-primary/10 text-primary border-primary/20" },
    viewed:        { label: "Viewed",        bg: "bg-sky-50 text-sky-700 border-sky-200" },
    partial:       { label: "Partly paid",   bg: "bg-amber-50 text-amber-800 border-amber-200" },
    paid:          { label: "Paid",          bg: "bg-emerald-50 text-emerald-800 border-emerald-200" },
    refunded:      { label: "Refunded",      bg: "bg-violet-50 text-violet-700 border-violet-200" },
    uncollectible: { label: "Uncollectible", bg: "bg-slate-100 text-slate-700 border-slate-300" },
    void:          { label: "Void",          bg: "bg-rose-50 text-rose-700 border-rose-200" },
};
const PAYMENT_METHODS = [
    { value: "cash", label: "Cash" },
    { value: "upi", label: "UPI" },
    { value: "card", label: "Card" },
    { value: "bank_transfer", label: "Bank transfer" },
    { value: "cheque", label: "Cheque" },
    { value: "other", label: "Other" },
];

function formatINR(amount) {
    if (amount === null || amount === undefined) return "—";
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(amount);
}

export default function InvoiceDetailDialog({ open, invoice, patientCases = [], onClose, onChanged }) {
    const { user } = useAuth();
    const [showEdit, setShowEdit] = useState(false);
    const [showPayment, setShowPayment] = useState(false);
    const [showRefund, setShowRefund] = useState(false);
    const [showPrint, setShowPrint] = useState(false);
    const [working, setWorking] = useState(false);
    const [sendingWhatsapp, setSendingWhatsapp] = useState(false);

    if (!invoice) return null;
    const tone = STATUS_TONE[invoice.status] || STATUS_TONE.sent;
    const canEdit = ["draft", "sent", "viewed", "partial"].includes(invoice.status);
    const canPay = ["draft", "sent", "viewed", "partial"].includes(invoice.status);
    const canVoid = ["draft", "sent", "viewed", "partial"].includes(invoice.status);
    /* iter98.99x — Refund is available whenever the invoice has any
     * paid amount left to refund, and isn't in a terminal state that
     * already settled the issue (void / uncollectible / refunded). */
    const canRefund =
        !["draft", "void", "uncollectible", "refunded"].includes(invoice.status)
        && (invoice.amount_paid || 0) - (invoice.amount_refunded || 0) > 0;
    const canUncollectible = ["sent", "viewed", "partial"].includes(invoice.status);

    const doAction = async (label, fn) => {
        setWorking(true);
        try {
            await fn();
            toast.success(label);
            onChanged && onChanged();
        } catch (e) {
            toast.error(e?.response?.data?.detail || `${label} failed`);
        } finally {
            setWorking(false);
        }
    };

    const voidIt = async () => {
        if (!(await confirmDialog({ title: "Void this invoice?", message: "This cannot be undone.", confirmText: "Void", tone: "danger" }))) return;
        doAction("Invoice voided", () => api.post(`/doctor/invoices/${invoice.id}/void`));
    };
    /* iter98.99x — Mark uncollectible. Different from void: this keeps
     * the invoice in the A/R history (the bill was correct, you've just
     * given up trying to collect). One-way action. */
    const markUncollectible = async () => {
        if (!(await confirmDialog({
            title: "Mark this invoice uncollectible?",
            message: "Use this when the bill was right but the patient won't pay (moved, ghosted, insurance denied). Different from voiding (which says the invoice was wrong). This cannot be undone.",
            confirmText: "Mark uncollectible",
            tone: "danger",
        }))) return;
        doAction("Marked uncollectible", () => api.post(`/doctor/invoices/${invoice.id}/uncollectible`));
    };

    /* WhatsApp this invoice — opens wa.me in a new tab with a pre-formatted
       message containing the patient name, invoice code, total, balance, and
       a friendly thank-you. The doctor's WhatsApp account sends it, so the
       patient sees the message coming from their clinic, not from Invisique.
       We lazily fetch patient phone (not stored on the invoice) so we always
       hit the freshest record. Strips +/spaces/dashes before passing to
       wa.me, which only accepts digits. */
    const sendOnWhatsApp = async () => {
        if (sendingWhatsapp) return;
        setSendingWhatsapp(true);
        try {
            const { data: patient } = await api.get(`/doctor/patients/${invoice.patient_id}`);
            const rawPhone = patient?.phone || "";
            const digits = rawPhone.replace(/\D/g, "");
            if (!digits) {
                toast.error("This patient has no phone number saved. Add one to their profile first.");
                return;
            }
            const clinic = user?.doctor_clinic_name || user?.clinic_name || "your clinic";
            const fmt = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

            const lines = [];
            lines.push(`Hello ${invoice.patient_name || ""},`);
            lines.push("");
            lines.push(`This is regarding your invoice from ${clinic}.`);
            lines.push("");
            lines.push(`*Invoice:* ${invoice.code}`);
            lines.push(`*Total:* ${fmt(invoice.total)}`);
            if (invoice.amount_paid > 0 && invoice.balance > 0) {
                lines.push(`*Paid so far:* ${fmt(invoice.amount_paid)}`);
                lines.push(`*Balance due:* ${fmt(invoice.balance)}`);
            } else if (invoice.balance > 0) {
                lines.push(`*Balance due:* ${fmt(invoice.balance)}`);
            } else if (invoice.status === "paid") {
                lines.push(`Payment received — *invoice fully settled.* Thank you!`);
            }
            if (invoice.due_at && invoice.balance > 0) {
                lines.push(`*Due by:* ${new Date(invoice.due_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}`);
            }
            lines.push("");
            if (invoice.balance > 0) {
                lines.push("Please reach out if you have any questions about the bill or would like to arrange payment.");
            }
            lines.push("");
            lines.push("Thank you,");
            lines.push(clinic);

            const text = encodeURIComponent(lines.join("\n"));
            window.open(`https://wa.me/${digits}?text=${text}`, "_blank", "noopener,noreferrer");
            toast.success("Opening WhatsApp…");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not open WhatsApp");
        } finally {
            setSendingWhatsapp(false);
        }
    };

    /* iter98.99ab (2026-06) — One-tap "Mark paid via UPI" shortcut.
     *
     * Chair-side reality: patient scans the UPI QR on the invoice and pays
     * the full balance instantly. The doctor wants to log that with ZERO
     * extra clicks — no dialog, no amount field, just mark it done.
     *
     * Flow:
     *   1. Click → POST /payments with amount=balance, method=upi.
     *   2. Toast surfaces with an Undo button that calls DELETE on the
     *      payment we just created (5-second window — sonner default).
     *   3. Invoice refreshes via onChanged.
     *
     * Only renders when the doctor has a UPI ID set, the invoice has
     * balance > 0, and the invoice is in a payable state. */
    const markPaidViaUpi = async () => {
        if (working) return;
        setWorking(true);
        const fmt = (n) => new Intl.NumberFormat("en-IN", {
            style: "currency", currency: "INR", maximumFractionDigits: 2,
        }).format(n || 0);
        try {
            const { data: updated } = await api.post(`/doctor/invoices/${invoice.id}/payments`, {
                amount: invoice.balance,
                method: "upi",
                paid_at: new Date().toISOString().slice(0, 10),
                reference: "",
                note: "One-tap mark paid via UPI",
            });
            // Find the payment we just appended (last one) so Undo can target it.
            const newPayment = (updated?.payments || []).slice(-1)[0];
            toast.success(`Paid · ${fmt(invoice.balance)} via UPI`, {
                action: newPayment ? {
                    label: "Undo",
                    onClick: async () => {
                        try {
                            await api.delete(`/doctor/invoices/${invoice.id}/payments/${newPayment.id}`);
                            toast.success("Payment reversed");
                            onChanged && onChanged();
                        } catch (e) {
                            toast.error(e?.response?.data?.detail || "Could not undo");
                        }
                    },
                } : undefined,
                duration: 6000,
            });
            onChanged && onChanged();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not mark paid");
        } finally {
            setWorking(false);
        }
    };

    return (
        <>
            <Dialog open={open && !showEdit && !showPrint} onOpenChange={(v) => !v && onClose()}>
                <DialogContent
                    className="max-w-[680px] max-h-[92vh] overflow-y-auto p-0 gap-0 rounded-2xl"
                    data-testid="invoice-detail-dialog"
                >
                    {/* ─── HERO ── Warm cream card header on our brand
                         palette. Icon in a soft primary tint, code + status
                         pill in the app's standard pill grammar, and the
                         balance amount reads via SIZE + weight rather than
                         a loud color splash. Matches every other detail
                         surface in the doctor portal. */}
                    <DialogHeader className="px-7 pt-6 pb-5 border-b border-border/60 bg-gradient-to-b from-secondary/60 to-secondary/10">
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex items-start gap-3 min-w-0">
                                <div className="w-11 h-11 rounded-xl bg-primary/10 border border-primary/15 flex items-center justify-center shrink-0">
                                    <Receipt size={22} weight="duotone" className="text-primary" />
                                </div>
                                <div className="min-w-0">
                                    <DialogTitle asChild>
                                        <h3 className="font-display text-lg font-semibold tracking-tight flex items-center gap-2 flex-wrap">
                                            <span className="font-mono text-[13.5px] text-foreground/85">{invoice.code}</span>
                                            <span
                                                className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${tone.bg}`}
                                                data-testid="invoice-detail-status"
                                            >
                                                <span className="w-1.5 h-1.5 rounded-full bg-current" />
                                                {tone.label}
                                            </span>
                                        </h3>
                                    </DialogTitle>
                                    <DialogDescription asChild>
                                        <p className="text-[12.5px] text-muted-foreground font-medium mt-1">
                                            Issued to <strong className="text-foreground">{invoice.patient_name}</strong>
                                            {invoice.issued_at && (
                                                <> · dated {new Date(invoice.issued_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</>
                                            )}
                                        </p>
                                    </DialogDescription>
                                </div>
                            </div>

                            {/* Amount block — sits on the right of the
                                hero. Size, not colour, does the work.
                                Balance-due amounts render in `text-primary`;
                                settled amounts in `text-foreground`. */}
                            <div className="text-right shrink-0 pr-8">
                                <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-0.5">
                                    {invoice.balance > 0 ? "Balance due" : invoice.status === "paid" ? "Amount received" : "Total"}
                                </div>
                                <div
                                    className={`font-display text-[28px] leading-none font-bold tabular-nums ${
                                        invoice.balance > 0 ? "text-primary" : "text-foreground"
                                    }`}
                                    data-testid="invoice-hero-balance"
                                >
                                    {formatINR(invoice.balance > 0 ? invoice.balance : invoice.total)}
                                </div>
                                {invoice.due_at && invoice.balance > 0 && (
                                    <div className="text-[11px] text-muted-foreground mt-1 font-medium">
                                        Due {new Date(invoice.due_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                                    </div>
                                )}
                            </div>
                        </div>
                    </DialogHeader>

                    <div className="px-7 pt-5 pb-6 space-y-5">

                    {/* LINE ITEMS — subtle secondary header, generous
                        row padding, hover-highlight matching the ledger. */}
                    <div className="rounded-xl border border-border overflow-hidden" data-testid="invoice-detail-line-items">
                        <div className="bg-secondary/60 border-b border-border px-5 py-2.5 grid grid-cols-12 gap-2 text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                            <div className="col-span-7">Description</div>
                            <div className="col-span-2 text-center">Qty</div>
                            <div className="col-span-3 text-right">Amount</div>
                        </div>
                        {(invoice.line_items || []).map((li, i) => (
                            <div
                                key={i}
                                className="grid grid-cols-12 gap-2 px-5 py-3.5 items-center border-b border-border/40 last:border-0 hover:bg-secondary/25 transition-colors"
                            >
                                <div className="col-span-7 min-w-0">
                                    <div className="font-semibold text-foreground text-sm">{li.description}</div>
                                    <div className="text-[11.5px] text-muted-foreground font-medium mt-0.5">{li.quantity} × {formatINR(li.unit_price)}</div>
                                </div>
                                <div className="col-span-2 text-center tabular-nums font-semibold text-foreground/85 text-sm">{li.quantity}</div>
                                <div className="col-span-3 text-right tabular-nums font-semibold text-foreground text-sm">{formatINR(li.line_total)}</div>
                            </div>
                        ))}
                    </div>

                    {/* SUMMARY — muted card with a primary-tinted Total
                        strip. Uses OUR primary teal (not emerald), keeping
                        the surface consistent with buttons & pills across
                        the app. */}
                    <div className="rounded-xl border border-border overflow-hidden bg-secondary/25" data-testid="invoice-detail-totals">
                        <div className="px-5 py-2.5 flex items-center justify-between text-sm border-b border-border/50">
                            <span className="text-muted-foreground">Subtotal</span>
                            <span className="tabular-nums font-medium text-foreground">{formatINR(invoice.subtotal)}</span>
                        </div>
                        {invoice.tax_amount > 0 && (
                            <div className="px-5 py-2.5 flex items-center justify-between text-sm border-b border-border/50">
                                <span className="text-muted-foreground">Tax ({invoice.tax_pct}%)</span>
                                <span className="tabular-nums font-medium text-foreground">{formatINR(invoice.tax_amount)}</span>
                            </div>
                        )}
                        {invoice.amount_paid > 0 && (
                            <div className="px-5 py-2.5 flex items-center justify-between text-sm border-b border-border/50">
                                <span className="text-emerald-700 font-medium">Paid</span>
                                <span className="tabular-nums font-medium text-emerald-800">{formatINR(invoice.amount_paid)}</span>
                            </div>
                        )}
                        {invoice.amount_refunded > 0 && (
                            <div className="px-5 py-2.5 flex items-center justify-between text-sm border-b border-border/50">
                                <span className="text-violet-700 font-medium">Refunded</span>
                                <span className="tabular-nums font-medium text-violet-800">-{formatINR(invoice.amount_refunded)}</span>
                            </div>
                        )}
                        {/* Total strip — primary background (Apollo teal),
                            larger amount. This is where the eye lands. */}
                        <div className="px-5 py-3.5 flex items-center justify-between bg-primary text-primary-foreground">
                            <span className="font-display font-semibold text-[15px] tracking-tight">
                                {invoice.balance > 0 && invoice.amount_paid > 0 ? "Balance" : "Total"}
                            </span>
                            <span className="tabular-nums font-display font-bold text-xl">
                                {formatINR(invoice.balance > 0 ? invoice.balance : invoice.total)}
                            </span>
                        </div>
                    </div>

                    {/* iter98.99aa — UPI QR snapshot. Only renders when the
                        doctor has a UPI ID set in Profile → Bank & UPI and
                        the invoice still has a balance to collect. Lets the
                        doctor show the screen to the patient on the spot. */}
                    {(user?.doctor_upi_id || "").trim() && invoice.balance > 0
                        && !["void", "uncollectible", "refunded"].includes(invoice.status) && (
                        <div
                            className="rounded-xl border-2 border-dashed border-primary/30 bg-primary/5 p-4 flex items-center gap-4"
                            data-testid="invoice-detail-upi-block"
                        >
                            <div className="bg-white p-1.5 rounded-md shrink-0 border border-border">
                                <QRCodeSVG
                                    value={`upi://pay?pa=${encodeURIComponent(user.doctor_upi_id.trim())}&pn=${encodeURIComponent(user.doctor_clinic_name || user.clinic_name || "")}&am=${(invoice.balance || 0).toFixed(2)}&cu=INR&tn=${encodeURIComponent("Invoice " + invoice.code)}`}
                                    size={84}
                                    level="M"
                                    includeMargin={false}
                                />
                            </div>
                            <div className="min-w-0">
                                <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-primary">
                                    <QrCode size={11} weight="duotone" /> Scan to pay · UPI
                                </div>
                                <div className="font-display text-base font-semibold mt-0.5 leading-tight">
                                    Pay {formatINR(invoice.balance)}
                                </div>
                                <div className="text-[11px] font-mono text-foreground/80 mt-1">
                                    {user.doctor_upi_id.trim()}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* PAYMENT HISTORY */}
                    {(invoice.payments || []).length > 0 && (
                        <div>
                            <Label className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Payments</Label>
                            <div className="mt-2 space-y-1.5">
                                {invoice.payments.map((p) => (
                                    <div key={p.id} className="flex items-center justify-between text-xs py-1.5 px-3 rounded-md bg-emerald-50/50 border border-emerald-100" data-testid={`payment-row-${p.id}`}>
                                        <div className="flex items-center gap-2">
                                            <CheckCircle size={12} weight="fill" className="text-emerald-600" />
                                            <span className="font-medium capitalize">{(p.method || "cash").replace("_", " ")}</span>
                                            <span className="text-muted-foreground">{new Date(p.paid_at).toLocaleDateString()}</span>
                                            {p.reference && <span className="text-muted-foreground font-mono">· {p.reference}</span>}
                                        </div>
                                        <span className="tabular-nums font-medium text-emerald-800">{formatINR(p.amount)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* iter98.99x — REFUND HISTORY (mirrors the payment list
                        styling in violet so it reads as the "money out"
                        counterpart of "money in"). */}
                    {(invoice.refunds || []).length > 0 && (
                        <div>
                            <Label className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Refunds</Label>
                            <div className="mt-2 space-y-1.5">
                                {invoice.refunds.map((r) => (
                                    <div key={r.id} className="flex items-center justify-between text-xs py-1.5 px-3 rounded-md bg-violet-50/50 border border-violet-100" data-testid={`refund-row-${r.id}`}>
                                        <div className="flex items-center gap-2">
                                            <ArrowsCounterClockwise size={12} weight="fill" className="text-violet-600" />
                                            <span className="font-medium capitalize">{(r.method || "upi").replace("_", " ")}</span>
                                            <span className="text-muted-foreground">{new Date(r.paid_at).toLocaleDateString()}</span>
                                            {r.reference && <span className="text-muted-foreground font-mono">· {r.reference}</span>}
                                        </div>
                                        <span className="tabular-nums font-medium text-violet-800">-{formatINR(r.amount)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* NOTES */}
                    {invoice.notes && (
                        <div className="text-xs text-muted-foreground italic border-l-2 border-border pl-3">
                            {invoice.notes}
                        </div>
                    )}

                    </div>

                    <DialogFooter className="px-7 pt-4 pb-6 mt-0 border-t border-border/60 bg-secondary/15 flex flex-col gap-4 sm:gap-4 sm:flex-col">
                        {/* Secondary action rail — OUR shadcn outline
                            buttons (rounded-full), color-coded via icon +
                            subtle text tint (not loud backgrounds). Matches
                            the muted button grammar used across the doctor
                            portal. */}
                        <div className="flex flex-wrap items-center justify-center gap-2" data-testid="invoice-detail-action-rail">
                            <Button variant="outline" size="sm" onClick={() => setShowPrint(true)} className="rounded-full" data-testid="invoice-print">
                                <Printer size={13} weight="duotone" className="mr-1.5" /> Print
                            </Button>
                            {invoice.status !== "void" && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={sendOnWhatsApp}
                                    disabled={sendingWhatsapp}
                                    className="rounded-full text-emerald-700 hover:text-emerald-800 border-emerald-200 hover:border-emerald-300 hover:bg-emerald-50"
                                    data-testid="invoice-whatsapp"
                                >
                                    <WhatsappLogo size={13} weight="duotone" className="mr-1.5" />
                                    {sendingWhatsapp ? "Opening…" : "WhatsApp"}
                                </Button>
                            )}
                            {canEdit && (
                                <Button variant="outline" size="sm" onClick={() => setShowEdit(true)} className="rounded-full" data-testid="invoice-edit">
                                    <Pencil size={12} weight="duotone" className="mr-1.5" /> Edit
                                </Button>
                            )}
                            {canVoid && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={voidIt}
                                    disabled={working}
                                    className="rounded-full text-rose-700 hover:text-rose-800 border-rose-200 hover:border-rose-300 hover:bg-rose-50"
                                    data-testid="invoice-void"
                                >
                                    <X size={12} weight="bold" className="mr-1.5" /> Void
                                </Button>
                            )}
                            {canUncollectible && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={markUncollectible}
                                    disabled={working}
                                    className="rounded-full text-slate-700 hover:text-slate-800 border-slate-300"
                                    data-testid="invoice-uncollectible"
                                >
                                    <ProhibitInset size={12} weight="duotone" className="mr-1.5" /> Mark uncollectible
                                </Button>
                            )}
                            {canRefund && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setShowRefund(true)}
                                    disabled={working}
                                    className="rounded-full text-violet-700 hover:text-violet-800 border-violet-200 hover:bg-violet-50"
                                    data-testid="invoice-refund"
                                >
                                    <ArrowsCounterClockwise size={12} weight="duotone" className="mr-1.5" /> Record refund
                                </Button>
                            )}
                        </div>

                        {/* Primary CTA — OUR default Button (primary bg,
                            rounded-full). Centered, generously sized so it
                            reads as THE decisive action.

                            iter98.99ab — Slim "Mark paid via UPI" shortcut
                            above the CTA. Chair-side patient who just
                            scanned the QR? One tap to log. Toast has an
                            Undo within 6s. */}
                        {canPay && (
                            <div className="flex flex-col items-center gap-2">
                                {canPay
                                    && invoice.balance > 0
                                    && (user?.doctor_upi_id || "").trim()
                                    && (
                                    <button
                                        type="button"
                                        onClick={markPaidViaUpi}
                                        disabled={working}
                                        className="text-[11.5px] text-primary hover:text-primary/80 hover:underline disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1 transition-colors"
                                        data-testid="invoice-mark-paid-upi"
                                        title="Records full balance as paid via UPI. You'll have 6 seconds to undo."
                                    >
                                        <QrCode size={11} weight="duotone" />
                                        {working
                                            ? "Recording…"
                                            : `Patient just paid ${formatINR(invoice.balance)} via UPI? Mark paid in one tap`}
                                    </button>
                                )}
                                <Button
                                    onClick={() => setShowPayment(true)}
                                    disabled={working}
                                    className="rounded-full px-10 h-11 text-sm bg-primary hover:bg-primary/90 shadow-sm"
                                    data-testid="invoice-record-payment"
                                >
                                    <Money size={14} weight="duotone" className="mr-1.5" /> Record payment
                                </Button>
                            </div>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <RecordRefundDialog
                open={showRefund}
                invoice={invoice}
                onClose={() => setShowRefund(false)}
                onSaved={() => { setShowRefund(false); onChanged && onChanged(); }}
            />

            <RecordPaymentDialog
                open={showPayment}
                invoice={invoice}
                onClose={() => setShowPayment(false)}
                onSaved={() => { setShowPayment(false); onChanged && onChanged(); }}
            />

            <InvoiceFormDialog
                open={showEdit}
                onClose={() => setShowEdit(false)}
                onSaved={() => { setShowEdit(false); onChanged && onChanged(); }}
                patientId={invoice.patient_id}
                patientName={invoice.patient_name}
                cases={patientCases}
                defaultCaseId={invoice.case_id}
                existingInvoice={invoice}
            />

            <InvoicePrintView
                open={showPrint}
                invoice={invoice}
                onClose={() => setShowPrint(false)}
            />
        </>
    );
}


/* iter98.99x — Sub-dialog: record one refund against the invoice.
 * Pre-fills the remaining refundable amount (amount_paid - amount_refunded)
 * and disallows over-refund client-side; the backend re-validates. */
function RecordRefundDialog({ open, invoice, onClose, onSaved }) {
    const remaining = Math.max(0, (invoice?.amount_paid || 0) - (invoice?.amount_refunded || 0));
    const [amount, setAmount] = useState("");
    const [method, setMethod] = useState("upi");
    const [reference, setReference] = useState("");
    const [note, setNote] = useState("");
    const [refundedAt, setRefundedAt] = useState(new Date().toISOString().slice(0, 10));
    const [submitting, setSubmitting] = useState(false);

    React.useEffect(() => {
        if (open && invoice) {
            setAmount(String(remaining || ""));
            setMethod("upi");
            setReference("");
            setNote("");
            setRefundedAt(new Date().toISOString().slice(0, 10));
        }
    }, [open, invoice, remaining]);

    const save = async () => {
        const amt = parseFloat(amount);
        if (!amt || amt <= 0) return;
        if (amt > remaining) {
            toast.error(`Refund cannot exceed remaining refundable amount (${formatINR(remaining)})`);
            return;
        }
        setSubmitting(true);
        try {
            await api.post(`/doctor/invoices/${invoice.id}/refunds`, {
                amount: amt,
                method,
                paid_at: refundedAt,
                reference: reference.trim(),
                note: note.trim(),
            });
            toast.success(`Refund recorded: ${formatINR(amt)}`);
            onSaved && onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to record refund");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-md" data-testid="record-refund-dialog">
                <DialogHeader>
                    <DialogTitle>Record refund</DialogTitle>
                    <DialogDescription>
                        Refundable: <span className="font-medium tabular-nums">{formatINR(remaining)}</span>
                        {(invoice?.amount_refunded || 0) > 0 && (
                            <span className="ml-2 text-violet-700">
                                · {formatINR(invoice.amount_refunded)} already refunded
                            </span>
                        )}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 mt-2">
                    <div>
                        <Label htmlFor="refund-amount" className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Amount</Label>
                        <Input
                            id="refund-amount"
                            type="number"
                            min="0"
                            max={remaining}
                            step="0.01"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            className="mt-1 text-lg tabular-nums"
                            data-testid="refund-amount"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label htmlFor="refund-method" className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Method</Label>
                            <select
                                id="refund-method"
                                value={method}
                                onChange={(e) => setMethod(e.target.value)}
                                className="mt-1 h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                                data-testid="refund-method"
                            >
                                {PAYMENT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                            </select>
                        </div>
                        <div>
                            <Label htmlFor="refund-date" className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Date</Label>
                            <Input
                                id="refund-date"
                                type="date"
                                value={refundedAt}
                                onChange={(e) => setRefundedAt(e.target.value)}
                                className="mt-1"
                                data-testid="refund-date"
                            />
                        </div>
                    </div>
                    <div>
                        <Label htmlFor="refund-reference" className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Reference (optional)</Label>
                        <Input
                            id="refund-reference"
                            value={reference}
                            onChange={(e) => setReference(e.target.value)}
                            placeholder="UPI ref, txn id, cheque no."
                            className="mt-1"
                            data-testid="refund-reference"
                        />
                    </div>
                    <div>
                        <Label htmlFor="refund-note" className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Note (optional)</Label>
                        <Textarea
                            id="refund-note"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            rows={2}
                            className="mt-1 text-sm"
                            data-testid="refund-note"
                        />
                    </div>
                </div>

                <DialogFooter className="mt-4">
                    <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
                    <Button
                        onClick={save}
                        disabled={!parseFloat(amount) || submitting}
                        className="rounded-full bg-violet-600 hover:bg-violet-700 text-white"
                        data-testid="refund-confirm"
                    >
                        {submitting ? "Saving…" : "Record refund"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
